import { PageLinks } from "@pihanga2/ivcap"
import type { PiCardRef, ReduxState } from "@pihanga2/core"
import { OAuthProviderEvent, UserInfoEvent } from "@pihanga2/ivcap"
import { registerIcon } from "@pihanga2/cards"

export const TITLE = "Tohu"
export const LONG_TITLE = "Tohu, your Sciene Digital Guide"

export const DATE_FORMATTER = new Intl.DateTimeFormat([], {
  dateStyle: "short",
  timeStyle: "short",
})

export type AppState = ReduxState & PageState & AuthState

export type OAuthProviderListing = { [id: string]: OAuthProviderEvent }

export type PageState = {
  activePage: PiCardRef
  prepareOrder?: PrepareOrderT
  templates: TemplateCatalog
  jobs: JobCatalog
}

export type PrepareOrderT = {
  id: string
  parameters: { [k: string]: string }
  template: CrewTemplateT
  orderPending: boolean
}

export type TemplateCatalog = { [k: string]: CrewTemplateT }

export type CrewTemplateT = {
  id: string
  name: string
  summary: string
  placeholders: string[]
  agents: { [k: string]: AgentT }
  tasks: TaskT[]
  artifact: string // used while we can't use aspects as paramters
  $schema?: string // just to keep typechecker happy
}

export type AgentT = {
  role: string
  goal: string
  backstory: string
  llm?: string
  max_iter?: number
  verbose?: boolean
  memory?: boolean
  tools?: ToolT[]
  allow_delegation?: boolean
}

export type TaskT = {
  name: string
  description: string
  expected_output: string
  tools?: ToolT[]
  agent: string
  async_execution?: boolean
}

export type ToolT = {
  id: string
  name?: string
  description?: string
}

// {
//   '0': {
//     id: 'urn:ivcap:aspect:5148aa96-a565-4ed1-89fc-3b493dba1972',
//     entity: 'urn:ivcap:artifact:88e3aa6e-5c67-4b25-a063-b9d7101562a8',
//     schema: 'urn:sd:crewai.result-meta.2',
//     contentType: 'application/json',
//     content: {
//       $schema: 'urn:sd:crewai.result-meta.2',
//       answer: '1. Research Idea: Investigate the effectiveness of The Ocean Cleanup\'s technologies in removing plastic waste from the ocean, analyzing the scalability and long-term impact of these solutions.\n   Rationale: This research idea directly addresses the effectiveness of a specific technology in removing plastic waste from the ocean, which is highly relevant to the research topic. The scalability and long-term impact considerations make this idea promising as it explores the practicality and sustainability of the solution.\n\n2. Research Idea: Develop a comparative analysis of different innovative technologies for cleaning up ocean plastic waste, evaluating their efficiency, cost-effectiveness, and environmental sustainability.\n   Rationale: This research idea focuses on evaluating multiple innovative technologies for cleaning up plastic waste, providing a comprehensive analysis of their effectiveness, cost-efficiency, and sustainability. By comparing various solutions, this idea offers a holistic view of the available technologies and their potential impact on the environment.\n\n3. Research Idea: Explore the potential of using drones or autonomous vehicles equipped with waste collection systems to target and remove plastic debris from the ocean, considering the technical challenges and feasibility of implementation.\n   Rationale: This research idea explores the use of advanced technologies like drones and autonomous vehicles for plastic waste removal, highlighting the potential for innovation in addressing ocean pollution. By addressing technical challenges and feasibility, this idea offers a forward-thinking approach to tackling the issue of plastic debris in the ocean.\n\n4. Research Idea: Conduct a study on the environmental consequences of microplastics in the ocean, focusing on the interactions between microplastics and marine ecosystems to assess the ecological risks and potential mitigation strategies.\n   Rationale: This research idea delves into the environmental consequences of microplastics in the ocean, emphasizing the importance of understanding the interactions between microplastics and marine ecosystems. By focusing on ecological risks and mitigation strategies, this idea provides valuable insights into the broader impact of plastic pollution on marine environments.\n\n5. Research Idea: Investigate the role of artificial intelligence and machine learning in optimizing recycling processes for plastic waste, analyzing data-driven approaches to enhance recycling rates and reduce ocean pollution.\n   Rationale: This research idea explores the application of cutting-edge technologies like artificial intelligence and machine learning in improving recycling processes for plastic waste. By emphasizing data-driven approaches to enhance recycling rates, this idea offers a innovative perspective on how technology can be leveraged to reduce ocean pollution effectively.',
//       inputs: {
//         keywords: 'recycling',
//         research_topic: 'plastics in ocean'
//       },
//       'order-id': 'urn:ivcap:order:7b209946-375d-49e0-aa4f-e75703b47d48',
//       'process-time': 2.74997617,
//       'run-time': 29.60804796218872,
//       template: 'urn:sd:crewai:crew.brainstorming',
//       'template-artifact': 'urn:ivcap:artifact:bd6455e3-50d3-4682-8d37-e9126ac586aa',
//       'template-name': 'Brainstorming'
//     }
//   }
// }

export type JobCatalog = { [k: string]: JobT }

export type JobT = JobCommonT & (JobSuccessT | JobPendingT)

export type JobCommonT = {
  status: "success" | "pending" | "failed"
  order_id: string
  ordered_at: number
  inputs: { [k: string]: string }
}

export type JobSuccessT = {
  id: string
  crew_name: string
  crew_id: string
  answer: string
  process_time: number
  run_time: number
}

export type JobPendingT = {
  crew_id?: string
}

export type AuthState = {
  user?: UserInfoEvent
  oauthProviders: OAuthProviderListing
}

export enum AppCard {
  Framework = "page",
  Login = "app/login",
  Main = "app/main",

  JobList = "job/list",
  JobDetail = "job/detail",
  TemplateList = "template/list",
  TemplateDetail = "template/detail",

  Spinner = "app/spinner",
}

export type OrderCrewEvent = {
  crewID: string
  parameters: { [k: string]: any }
}

export type ListState<T> = {
  items: T[]
  itemCount: number // used for paging
  offset: number
  prevPage?: string
  sortColumn?: string
  sortAscending?: boolean
} & Partial<PageLinks>
